import { useCallback } from 'react';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { bookReservation, holdOffer, releaseHold } from '@/lib/booking/api';
import { OfferExpiredError } from '@/lib/booking/errors';
import { isPriced } from '@/lib/allocation/price';
import { stayWindow } from '@/lib/allocation/stay';
import { useCaseStay } from '@/lib/allocation/use-stay';
import type { HotelOption, IPolicyOverride } from '@/lib/allocation/types';
import type { Reservation } from '@/lib/booking/types';
import type { ConfirmedAssignment, PendingHold } from '@/components/allocation/types';
import { newIdempotencyKey } from '@/lib/uuid';

// HOLD is the operator UI's OWN state — it never appears in reservations-by-case
// (that poll only returns the durable PENDING/BOOKED/CANCELLED/FAILED/CANCEL_FAILED
// rows). The 2-step flow is: Reserve = POST /v1/booking/hold → we keep the
// attemptUrn here as a HOLD card; [Confirm booking] = POST /v1/booking/book
// (settles via the reservations poll); [Release] = POST /v1/booking/hold/release.
//
// The soft-hold's deadline is SERVER truth: /v1/booking/hold answers with
// `expiresAt` (+ the granted `ttlSeconds` and whose inventory `source` it is)
// and we store it verbatim on the card. We do NOT derive one. The UI used to
// fabricate `heldAt + 90s`, a constant that matched no deployment (booking
// grants 300s in prod, 1800s on the local stack), so cards announced "Hold
// expired" with most of the hold still live.
//
// When the response carries no clock the card reads "Hold active" with no
// countdown and keeps every action — absence of a deadline is not an expired
// deadline. Against a live booking domain that path is unreachable: booking
// 422s rather than granting a clockless hold (the 422 only fires on a genuine
// expiry race, where the operator's retry succeeds). It IS reachable in the
// BFF's offline/fixture mode, which is a real mode on this stack.
//
// Granted TTLs are 300s in prod and 1800s on the local stack, so the countdown
// has to read well at both scales ("4m 58s" and "30m 00s").
//
// A lapsed soft hold is informational, never terminal: Confirm stays available
// because acceptance-after-expiry is first-class server side and the accept path
// re-acquires. If a Confirm does race a hold the server already reaped, /v1/book
// 4xx's and we surface that on the card.

// HOLD lifecycle types live in the reusable allocation family now; re-exported
// here so existing case-tab imports keep working unchanged.
export type { ConfirmedAssignment, PendingHold } from '@/components/allocation/types';

type Action =
  | { type: 'add'; hold: PendingHold }
  | { type: 'patch'; id: string; patch: Partial<PendingHold> }
  | { type: 'remove'; id: string };

function reducer(state: PendingHold[], action: Action): PendingHold[] {
  switch (action.type) {
    case 'add':
      return [action.hold, ...state];
    case 'patch':
      return state.map((h) => (h.id === action.id ? { ...h, ...action.patch } : h));
    case 'remove':
      return state.filter((h) => h.id !== action.id);
  }
}

/**
 * The vendor's free-cancellation deadline for an offer, when the hotels view
 * supplied one.
 *
 * Absence is ordinary, not an error: fixture/offline offers deliberately carry
 * no deadline, which means "no stated cutoff" — so we send nothing and let
 * booking apply its own TTL. The runtime guard stays despite the field now
 * being typed: this value crosses the wire, and a stray '' or null from a
 * vendor adapter must never reach booking dressed as a deadline.
 */
function offerCancellationDeadline(offer: HotelOption): string | undefined {
  const value: unknown = offer.cancellationDeadline;
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/**
 * The operator-facing reason a hold action failed. This module holds KEYS, never
 * language — the caller's `t` (the `caseWork` namespace) renders them, because
 * the message is stored on the card and displayed verbatim by two different
 * hold rows.
 */
function holdExpiredMessage(err: unknown, t: TFunction<'caseWork'>): string {
  // booking@v2 has no live quote for this offer + stay: search again (§6.15).
  if (err instanceof OfferExpiredError) return t('holds.offerExpired');
  // ky throws HTTPError with a .response; a 409/410/404 means the hold is gone.
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status && status >= 400 && status < 500) {
    return t('holds.expiredOrUsed');
  }
  return t('holds.confirmFailed');
}

const reservationsKey = (caseUrn: string) => ['cases', caseUrn, 'reservations'] as const;
const holdStateKey = (caseUrn: string) => ['cases', caseUrn, 'manual-hold-state'] as const;

interface IHoldState {
  holds: PendingHold[];
  confirmed: Record<string, ConfirmedAssignment>;
}

const emptyHoldState = (): IHoldState => ({ holds: [], confirmed: {} });

export interface UsePendingHoldsOptions {
  /** `totalFormatted` = the airline's price for the stay as shown, `null` when unpriced. */
  onSuccess?: (info: { hotelName: string; rooms: number; totalFormatted: string | null }) => void;
}

/**
 * Session HOLD state for a case. The drawer and allocation board subscribe to
 * the same cache entry, so closing a drawer cannot forget its server-side hold.
 * It survives unmounting for the QueryClient lifetime, without persisting
 * passenger or contact data to browser storage. A full reload needs a server read.
 */
export function usePendingHolds(caseUrn: string, options?: UsePendingHoldsOptions) {
  const { t } = useTranslation('caseWork');
  const qc = useQueryClient();
  // The case's per-group stay (feature `stay-nights`, N8 — this is what closes
  // NEX-265). Confirm used to book a hardcoded `now → now + 24h` with the TODO
  // "Interim stay window (1 night from now) until the wizard captures real stay
  // dates". There is no wizard: the dates were never the operator's to type. cases
  // derives them from when the passenger next flies, and confirm now books THAT.
  const caseStay = useCaseStay(caseUrn);
  const { data: holdState } = useQuery({
    queryKey: holdStateKey(caseUrn),
    queryFn: skipToken,
    initialData: emptyHoldState,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const { holds, confirmed } = holdState ?? emptyHoldState();
  const dispatch = useCallback((action: Action): void => {
    qc.setQueryData<IHoldState>(holdStateKey(caseUrn), (prev = emptyHoldState()) => ({
      ...prev,
      holds: reducer(prev.holds, action),
    }));
  }, [caseUrn, qc]);

  /** Drop a confirmed-assignment record (e.g. once its reservation is cancelled). */
  const clearConfirmed = useCallback((subCaseUrn: string): void => {
    qc.setQueryData<IHoldState>(holdStateKey(caseUrn), (prev = emptyHoldState()) => {
      if (!(subCaseUrn in prev.confirmed)) return prev;
      const next = { ...prev.confirmed };
      delete next[subCaseUrn];
      return { ...prev, confirmed: next };
    });
  }, [caseUrn, qc]);

  /**
   * Reserve (hold-only). Returns nothing useful to the caller; surfaces as a HOLD card.
   * `policyOverride` — an offer outside the policy, chosen with a reason: it rides on the
   * card to the book (the hold itself never re-checks the policy).
   */
  const placeHold = useCallback(
    async (
      offer: HotelOption,
      subCaseUrn: string,
      roomCount: number,
      policyOverride?: IPolicyOverride,
    ): Promise<void> => {
      // Conditional spread, not `cancellationDeadline: undefined`: an offer
      // without a deadline must leave the request body byte-identical to before.
      const deadline = offerCancellationDeadline(offer);
      // The stay the offer was QUOTED for wins (booking prices from that quote, §6.15);
      // the group's plan only when the offer carries none.
      const plan = caseStay.groups.find((g) => g.subCaseUrn === subCaseUrn)?.stayPlan;
      const stay = offer.searchedStay ?? (plan ? stayWindow(plan) : undefined);
      const res = await holdOffer({
        hotelName: offer.name,
        ...(stay ? { checkInUtc: stay.checkInUtc, checkOutUtc: stay.checkOutUtc } : {}),
        offerUrn: offer.offerUrn,
        caseUrn,
        // NEX-451: two fields, two meanings — `offerCapacity` is the offer's
        // advertised capacity (booking's inventory seed); `rooms` is what this
        // group is requesting (the #484 per-group cap guards it).
        offerCapacity: offer.availableRooms,
        rooms: roomCount,
        subCaseUrn,
        ...(deadline ? { cancellationDeadline: deadline } : {}),
      });
      dispatch({
        type: 'add',
        hold: {
          id: newIdempotencyKey(),
          attemptUrn: res.attemptUrn,
          offer,
          subCaseUrn,
          roomCount,
          heldAt: Date.now(),
          expiresAt: res.expiresAt,
          ttlSeconds: res.ttlSeconds,
          source: res.source,
          bookKey: newIdempotencyKey(),
          ...(policyOverride ? { policyOverride } : {}),
        },
      });
    },
    [caseUrn, caseStay.groups, dispatch],
  );

  /** [Confirm booking] — book the held offer. On success the card is dropped and the poll takes over. */
  const confirm = useCallback(
    async (hold: PendingHold): Promise<void> => {
      dispatch({ type: 'patch', id: hold.id, patch: { busy: 'confirm', error: undefined } });
      // The window THIS group is stranded for. A group cases derived no plan for
      // falls back to the same one-night stay starting now that this call site
      // hardcoded before, so nothing regresses; a group with real evidence finally
      // books every night it needs.
      //
      // No `total` is sent: the rate on screen is the airline's PRICE, and a price never
      // travels back to booking as Nexa's cost (commercial-contract D16) — booking prices
      // the reservation from the offer, whole stay (§6.15). The figure below only seeds
      // the optimistic row until the reservations poll brings the real (priced) total.
      //
      // The window is the one the offer was QUOTED for (`searchedStay`) — booking prices
      // from the quote parked for that offer + stay; recomputing a default `now → +24h`
      // here could cross UTC midnight and name a stay booking never quoted (409
      // `offer_expired`). The group's plan only for an offer that carries no stay.
      const plan = caseStay.groups.find((g) => g.subCaseUrn === hold.subCaseUrn)?.stayPlan;
      const stay = hold.offer.searchedStay ?? stayWindow(plan);
      const nights = Math.max(1, stay.nights ?? 1);
      const shownTotal = isPriced(hold.offer.ratePerRoomUSD)
        ? Math.round(hold.offer.ratePerRoomUSD * hold.roomCount * nights * 100) / 100
        : null;
      try {
        const ref = await bookReservation(
          {
            caseUrn,
            subCaseUrn: hold.subCaseUrn,
            offerUrn: hold.offer.offerUrn,
            attemptUrn: hold.attemptUrn,
            hotelUrn: hold.offer.hotelUrn,
            hotelName: hold.offer.name,
            // B1 (#820): the offer's coordinates ride onto the reservation so the
            // transport voucher fence can resolve the booked hotel; never invented.
            ...(hold.offer.geo ? { hotel: { name: hold.offer.name, geo: hold.offer.geo } } : {}),
            rooms: hold.roomCount,
            guests: hold.roomCount,
            checkInUtc: stay.checkInUtc,
            checkOutUtc: stay.checkOutUtc,
            currency: 'USD',
            ...(hold.policyOverride ? { policyOverride: hold.policyOverride } : {}),
          },
          hold.bookKey,
        );
        dispatch({ type: 'remove', id: hold.id });
        // Seed an optimistic PENDING row so the reservations poll engages
        // immediately (NEX: book is async — the durable row can lag the 202 ack
        // by a beat; without this seed the refetch-on-PENDING predicate may see
        // an empty list first and never start polling, leaving the badge stuck).
        const now = new Date().toISOString();
        const optimistic: Reservation = {
          reservationUrn: ref.reservationUrn,
          status: ref.status ?? 'PENDING',
          hotelUrn: hold.offer.hotelUrn,
          hotelName: hold.offer.name,
          rooms: hold.roomCount,
          guests: hold.roomCount,
          total: shownTotal,
          currency: 'USD',
          createdAt: now,
          updatedAt: now,
        };
        qc.setQueryData<Reservation[]>(reservationsKey(caseUrn), (prev) => {
          const rows = Array.isArray(prev) ? prev : [];
          return rows.some((r) => r.reservationUrn === optimistic.reservationUrn)
            ? rows
            : [...rows, optimistic];
        });
        qc.setQueryData<IHoldState>(holdStateKey(caseUrn), (prev = emptyHoldState()) => ({
          ...prev,
          confirmed: {
            ...prev.confirmed,
            [hold.subCaseUrn]: {
              subCaseUrn: hold.subCaseUrn,
              hotelUrn: hold.offer.hotelUrn,
              hotelName: hold.offer.name,
              reservationUrn: ref.reservationUrn,
              rooms: hold.roomCount,
            },
          },
        }));
        options?.onSuccess?.({
          hotelName: hold.offer.name,
          rooms: hold.roomCount,
          totalFormatted: shownTotal === null ? null : `$${shownTotal.toLocaleString()}`,
        });
        void qc.invalidateQueries({ queryKey: ['cases', caseUrn] });
      } catch (err) {
        dispatch({
          type: 'patch',
          id: hold.id,
          patch: { busy: undefined, error: holdExpiredMessage(err, t) },
        });
      }
    },
    [caseUrn, qc, options, t, caseStay.groups, dispatch],
  );

  /** [Release] — drop the soft-hold server-side, then remove the card. */
  const release = useCallback(
    async (hold: PendingHold): Promise<void> => {
      dispatch({ type: 'patch', id: hold.id, patch: { busy: 'release', error: undefined } });
      try {
        await releaseHold(hold.offer.offerUrn, caseUrn, hold.subCaseUrn);
      } catch {
        dispatch({ type: 'patch', id: hold.id, patch: { busy: undefined, error: t('holds.releaseFailed') } });
        return;
      }
      dispatch({ type: 'remove', id: hold.id });
    },
    [caseUrn, t, dispatch],
  );

  /** [Re-hold] — re-acquire the soft hold; adopts the NEW server deadline (or
   *  drops back to no-clock if this grant carries none) + clears the error. */
  const rehold = useCallback(
    async (hold: PendingHold): Promise<void> => {
      dispatch({ type: 'patch', id: hold.id, patch: { busy: 'rehold', error: undefined } });
      try {
        const deadline = offerCancellationDeadline(hold.offer);
        const plan = caseStay.groups.find((g) => g.subCaseUrn === hold.subCaseUrn)?.stayPlan;
        const stay = plan ? stayWindow(plan) : undefined;
        const res = await holdOffer({
          hotelName: hold.offer.name,
          ...(stay ? { checkInUtc: stay.checkInUtc, checkOutUtc: stay.checkOutUtc } : {}),
          offerUrn: hold.offer.offerUrn,
          caseUrn,
          offerCapacity: hold.offer.availableRooms,
          subCaseUrn: hold.subCaseUrn,
          ...(deadline ? { cancellationDeadline: deadline } : {}),
        });
        dispatch({
          type: 'patch',
          id: hold.id,
          patch: {
            attemptUrn: res.attemptUrn,
            heldAt: Date.now(),
            expiresAt: res.expiresAt,
            ttlSeconds: res.ttlSeconds,
            source: res.source,
            busy: undefined,
            error: undefined,
          },
        });
      } catch (err) {
        dispatch({
          type: 'patch',
          id: hold.id,
          patch: { busy: undefined, error: holdExpiredMessage(err, t) },
        });
      }
    },
    [caseUrn, t, caseStay.groups, dispatch],
  );

  /** Dismiss a (usually expired) card without touching the server. */
  const dismiss = useCallback((hold: PendingHold): void => {
    dispatch({ type: 'remove', id: hold.id });
  }, [dispatch]);

  return { holds, confirmed, clearConfirmed, placeHold, confirm, release, rehold, dismiss };
}
